import { useQuery } from "@tanstack/react-query";
import { workQueuesApi } from "@/api/compliance/client";
import type { WorkQueueSeverity } from "@/api/compliance/workQueueTypes";
import { formatCurrency } from "@/design-system/lib/format";
import { WidgetFrame } from "../../WidgetFrame";
import type { DashboardWidgetProps } from "../../types";
import { PanelClearLine, PanelFigures, PanelRows } from "./shared";

/**
 * Governance cockpit panels: one summary-figures + top-rows widget per
 * cockpit (Compliance / QA / Defaults & Workout), each backed by the same
 * read-only work-queue endpoint as its full-page counterpart.
 */

const ROW_LIMIT = 5;

function severityTone(severity: WorkQueueSeverity | null): "danger" | "warn" | "neutral" {
  if (severity === "error") return "danger";
  if (severity === "warning") return "warn";
  return "neutral";
}

export function ComplianceWorkQueueWidget({ widget }: DashboardWidgetProps) {
  const queue = useQuery({
    queryKey: ["dashboard", "compliance-work-queue"],
    queryFn: () => workQueuesApi.compliance(),
  });
  const summary = queue.data?.summary;
  const rows = (queue.data?.rows ?? []).slice(0, ROW_LIMIT);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/compliance"
      toLabel="Open cockpit"
      isLoading={queue.isLoading}
      isError={queue.isError}
    >
      {summary && (
        <div className="flex flex-col gap-space-3">
          <PanelFigures
            figures={[
              { label: "due this week", value: summary.dueThisWeek, tone: "warning" },
              { label: "overdue", value: summary.overdue, tone: "error" },
              { label: "open exceptions", value: summary.openExceptions, tone: "warning" },
              { label: "material events", value: summary.openMaterialEvents },
            ]}
          />
          {rows.length === 0 ? (
            <PanelClearLine text="The compliance queue is clear." />
          ) : (
            <PanelRows
              items={rows.map((r) => ({
                id: r.id,
                primary: r.title,
                secondary: [r.loanNumber, r.detail].filter(Boolean).join(" · ") || null,
                badge: r.type,
                badgeTone: severityTone(r.severity),
                timestamp: r.dueDate,
                href: r.loanId ? `/portfolio-admin/loans/${r.loanId}?tab=${r.targetTab}` : null,
              }))}
            />
          )}
        </div>
      )}
    </WidgetFrame>
  );
}

export function QaWorkQueueWidget({ widget }: DashboardWidgetProps) {
  const queue = useQuery({
    queryKey: ["dashboard", "qa-work-queue"],
    queryFn: () => workQueuesApi.qa(),
  });
  const summary = queue.data?.summary;
  const rows = (queue.data?.rows ?? []).slice(0, ROW_LIMIT);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/quality-assurance"
      toLabel="Open cockpit"
      isLoading={queue.isLoading}
      isError={queue.isError}
    >
      {summary && (
        <div className="flex flex-col gap-space-3">
          <PanelFigures
            figures={[
              { label: "reviews due", value: summary.reviewsDue, tone: "warning" },
              { label: "overdue", value: summary.reviewsOverdue, tone: "error" },
              { label: "open exceptions", value: summary.openExceptions, tone: "warning" },
              { label: "awaiting tasks", value: summary.awaitingTaskCompletion },
            ]}
          />
          {rows.length === 0 ? (
            <PanelClearLine text="The QA queue is clear." />
          ) : (
            <PanelRows
              items={rows.map((r) => ({
                id: r.id,
                primary: r.title,
                secondary: [r.loanNumber, r.detail].filter(Boolean).join(" · ") || null,
                badgeTone: severityTone(r.severity),
                badge: r.type === "ReviewDue" ? "Review" : "Exception",
                timestamp: r.dueDate,
                href: `/portfolio-admin/loans/${r.loanId}?tab=legal-ops`,
              }))}
            />
          )}
        </div>
      )}
    </WidgetFrame>
  );
}

export function DefaultsWorkQueueWidget({ widget }: DashboardWidgetProps) {
  const queue = useQuery({
    queryKey: ["dashboard", "defaults-work-queue"],
    queryFn: () => workQueuesApi.defaults(),
  });
  const summary = queue.data?.summary;
  const rows = (queue.data?.rows ?? []).slice(0, ROW_LIMIT);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/defaults-workout"
      toLabel="Open cockpit"
      isLoading={queue.isLoading}
      isError={queue.isError}
    >
      {summary && (
        <div className="flex flex-col gap-space-3">
          <PanelFigures
            figures={[
              { label: "open cases", value: summary.openCases },
              { label: "exposure", value: formatCurrency(summary.totalExposure) },
              { label: "deadlines overdue", value: summary.deadlineOverdue, tone: "error" },
              {
                label: "pending approvals",
                value: summary.casesWithPendingApproval,
                tone: "warning",
              },
            ]}
          />
          {rows.length === 0 ? (
            <PanelClearLine text="No open default cases." />
          ) : (
            <PanelRows
              items={rows.map((r) => ({
                id: r.caseId,
                primary: r.borrowerName || r.loanNumber || "Default case",
                secondary: [r.loanNumber, r.status].filter(Boolean).join(" · ") || null,
                badge: `${r.daysInStage}d`,
                badgeTone: severityTone(r.severity),
                href: `/portfolio-admin/loans/${r.loanId}?tab=defaults`,
              }))}
            />
          )}
        </div>
      )}
    </WidgetFrame>
  );
}
